import type { VideoComposition } from "@hightouch/ad-studio-renderer";
import { resolveProxyPreferredFilename } from "src/pages/ad-studio/video-proxy";

import {
  resolveComponentAssets,
  rewriteAgentsFilesUrls,
} from "src/pages/ad-studio/presigned-urls";

interface PreResolveOptions {
  composition: VideoComposition;
  componentAssets: Record<string, string> | null | undefined;
  apiEndpoint: string;
  /**
   * Optional caller-provided resolver. When it returns inline content
   * (a string starting with `<`) for an `ad:` ref, that override is sent
   * to the iframe in `resolvedRefs`. Other custom-resolver return values
   * are ignored — the iframe falls back to `componentAssets` lookup.
   */
  customResolver?: (ref: string) => string;
}

interface PreResolveResult {
  /** Composition is shipped unchanged; the iframe's resolver looks up at render time. */
  composition: VideoComposition;
  /** Filename → presigned URL. Default fallback for any `ad:<filename>` ref. */
  componentAssets: Record<string, string>;
  /** Full `ad:` ref → resolved value. Overrides for inline content from the live editor. */
  resolvedRefs: Record<string, string>;
}

/**
 * Resolve every S3 key in `componentAssets` to a presigned URL, plus
 * collect any inline-content overrides from the caller's `customResolver`.
 *
 * Composition data is NOT rewritten — the iframe ships a real asset
 * resolver that consults the maps at render time. This keeps nested
 * `ad:` refs inside SVG documents working (they're resolved by the
 * renderer as it walks the parsed SVG, not at the composition level).
 *
 * Presigned URLs are cached per S3 key. resolveS3KeyCached re-signs when
 * the cached URL is past Expires.
 */
export async function preResolveAssets({
  composition,
  componentAssets,
  apiEndpoint,
  customResolver,
}: PreResolveOptions): Promise<PreResolveResult> {
  // `byS3Key` feeds `rewriteAgentsFilesUrls` below, which swaps
  // `/agents/files?key=...` URLs (which the editor's `resolveAdRefs` /
  // `resolveAdUseRefs` bakes into SVG layers when they enter the editor —
  // see `packages/app/src/pages/ad-studio/layers/utils.ts`) for presigned
  // URLs the iframe can actually fetch without cookies.
  const { byFilename, byS3Key } = await resolveComponentAssets({
    componentAssets,
    apiEndpoint,
    targetRenderer: "hightouch-webapp",
  });
  const previewAssets = Object.fromEntries(
    Object.entries(byFilename).map(([filename, url]) => {
      const preferredFilename = resolveProxyPreferredFilename(
        filename,
        componentAssets,
      );
      return [filename, byFilename[preferredFilename] ?? url];
    }),
  );

  // Inline overrides only — these come from the live-edit flow where the
  // editor hands the player an in-memory SVG string for layers being
  // actively edited. Non-inline custom returns are ignored; the iframe's
  // resolver will fall back to `componentAssets` lookup which produces
  // the same presigned URL anyway.
  const resolvedRefs: Record<string, string> = {};

  if (customResolver) {
    for (const ref of collectTopLevelAdRefs(composition)) {
      const custom = customResolver(ref);
      if (custom.startsWith("<")) {
        resolvedRefs[ref] = rewriteAgentsFilesUrls(custom, byS3Key, "xml");
      }
    }
  }

  return {
    composition,
    componentAssets: previewAssets,
    resolvedRefs,
  };
}

function collectTopLevelAdRefs(composition: VideoComposition): string[] {
  const refs = new Set<string>();
  const visit = (value: unknown): void => {
    if (typeof value === "string" && value.startsWith("ad:")) {
      refs.add(value);
    } else if (Array.isArray(value)) {
      for (const item of value) visit(item);
    } else if (value !== null && typeof value === "object") {
      for (const v of Object.values(value as Record<string, unknown>)) visit(v);
    }
  };
  for (const layer of composition.layers) visit(layer.props);
  return [...refs];
}
